import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import BooksSubmitButton from '../../../../components/books/common/buttons/BooksSubmitButton';
import BooksLoginSuggestAlert from '../../../../components/books/model1/texts/BooksLoginSuggestAlert';
import BooksWithoutLoginAlert from '../../../../components/books/model1/texts/BooksWithoutLoginAlert';
import {
  UserContextConsumer,
  UserContextState,
} from '../../../../contexts/UserContext';
import {
  LoginQuery,
  LoginWithFbQuery,
} from '../../../../infrastructure/repositories/AuthRepository';
import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';
import {
  setMeToBookInfo,
  setPostable,
} from '../../../../redux/actions/merchants/resources/books';
import {
  clearUserInfoError,
  inputUserInfo,
  RestartReservationError,
} from '../../../../redux/actions/merchants/resources/books/inputs';
import * as workflow from '../../../../redux/actions/merchants/resources/books/workflow';
import { Dispatch } from '../../../../redux/reducers/merchants/resources/books/inputs';
import scrollToTop from '../../../hoc/scrollToTop';
import * as $ from './helper';
import InputLogin from './InputLogin';
import InputUserInfoCustomerFields, {
  createInitialState as customerFieldsInitialState,
  submittable as customerFieldsSubmittable,
} from './InputUserInfoCustomerFields';
import InputUserInfoQuestionnaires, {
  submittable as questionnairesSubmittable,
} from './InputUserInfoQuestionnaires';
import InputUserInfoSurvey, {
  submittable as surveySubmittable,
} from './InputUserInfoSurvey';

interface ElementProps {
  modelType: IBooks.ModelType;
  settings: IBooks.SettingUserInfo;
  postable: boolean;
  onSubmit: () => void;
  onRestart: () => void;
  submitLabelIntlId: string;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    handleSubmit: (
      reservationId: number,
      userInfo: IInputs.UserInfo,
      settings: IBooks.SettingUserInfo,
    ) => Promise<void>;
    handleChange: (name: string) => void;
    handleSetMe: (merchantId: string, resourceId: string) => Promise<void>;
    handlePostable: (postable: boolean) => void;
  };
}

enum FieldNames {
  SurveyAnswer = 'surveyAnswer',
  CustomerFields = 'customerFields',
  Questionnaires = 'questionnaires',
}

interface Control {
  submittable?: boolean;
}

export type InputValueType = string | boolean;

interface State {
  loginForm: boolean;
  [FieldNames.CustomerFields]?: IInputs.CustomerFieldsValueType;
  [FieldNames.SurveyAnswer]?: string;
  [FieldNames.Questionnaires]?: IInputs.QuestionnaireValueType;
  control: Control;
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

class InputUserInfo extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.handleSwitchView = this.handleSwitchView.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleLogin = this.handleLogin.bind(this);
    this.createControl = this.createControl.bind(this);
    this.createInitialState = this.createInitialState.bind(this);

    this.state = this.createInitialState();
  }

  private handleSwitchView() {
    const { loginForm } = this.state;
    workflow.tick({
      onForwardCallback: () => {
        this.setState({ ...this.state, loginForm: true });
      },
      onBackCallback: () => {
        this.setState({ ...this.state, loginForm: false });
      },
    });
    this.setState({ loginForm: !loginForm });
  }

  private handleChange(name: string, value: InputValueType) {
    const changedState = $.changeObjectByNestedString(this.state, name, value);
    this.setState({
      ...changedState,
      control: this.createControl(changedState as State),
    });
    this.props.actions.handleChange(name);
  }

  private async handleSubmit() {
    const { settings, inputs, actions, onSubmit, onRestart } = this.props;
    try {
      await actions.handleSubmit(inputs.reservationId!, this.state, settings);
      onSubmit();
    } catch (e) {
      if (e instanceof RestartReservationError) {
        onRestart();
      }
    }
  }

  private async handleLogin(_q: LoginQuery, userCtx: UserContextState) {
    const { inputs, actions } = this.props;
    actions.handlePostable(false);
    userCtx.auth
      .login(_q.email, _q.password)
      .then(async _ => {
        await actions.handleSetMe(inputs.merchantId!, inputs.resourceId!);
        this.setState({ loginForm: false });
        actions.handlePostable(true);
      })
      .catch((_err: Error) => {
        actions.handlePostable(true);
      });
  }

  private async handleLoginWithFb(
    query: LoginWithFbQuery,
    userCtx: UserContextState,
  ) {
    const { inputs, actions } = this.props;
    actions.handlePostable(false);
    userCtx.auth
      .loginWithFb(query)
      .then(async _ => {
        await actions.handleSetMe(inputs.merchantId!, inputs.resourceId!);
        this.setState({ loginForm: false });
        actions.handlePostable(true);
      })
      .catch((_err: Error) => {
        actions.handlePostable(true);
      });
  }

  private createInitialState(): State {
    const { inputs, settings } = this.props;
    const _state: State = { ...inputs.userInfo, control: {}, loginForm: false };
    if (!_state[FieldNames.CustomerFields]) {
      _state[FieldNames.CustomerFields] = customerFieldsInitialState(
        settings.customerFields,
      );
    }
    return { ..._state, control: this.createControl(_state) };
  }

  private createControl(state: State): Control {
    const { settings } = this.props;
    const { control } = state;
    control.submittable =
      customerFieldsSubmittable(
        settings.customerFields,
        $.getValueByNestedString(state, FieldNames.CustomerFields),
      ) &&
      surveySubmittable(
        settings.survey,
        $.getValueByNestedString(state, FieldNames.SurveyAnswer),
      ) &&
      questionnairesSubmittable(
        settings.questionnaires,
        $.getValueByNestedString(state, FieldNames.Questionnaires),
      );
    return control;
  }

  public render() {
    const { intl, settings, postable, submitLabelIntlId } = this.props;
    const {
      control: { submittable },
      loginForm,
    } = this.state;
    return (
      <UserContextConsumer>
        {context =>
          loginForm ? (
            <>
              <BooksWithoutLoginAlert onClick={this.handleSwitchView} />
              <InputLogin
                postable={postable}
                onLogin={(query: LoginQuery) =>
                  this.handleLogin(query, context)
                }
                onLoginWithFb={(query: LoginWithFbQuery) =>
                  this.handleLoginWithFb(query, context)
                }
                errors={context.auth.errors}
              />
            </>
          ) : (
            <>
              {!context.auth.isLoggedIn && (
                <BooksLoginSuggestAlert onClick={this.handleSwitchView} />
              )}
              {settings.customerFields && (
                <InputUserInfoCustomerFields
                  customerFields={settings.customerFields}
                  onChange={this.handleChange}
                  namePrefix={FieldNames.CustomerFields}
                  values={
                    $.getValueByNestedString(
                      this.state,
                      FieldNames.CustomerFields,
                    ) || {}
                  }
                />
              )}
              {settings.survey && (
                <InputUserInfoSurvey
                  survey={settings.survey}
                  onChange={this.handleChange}
                  name={FieldNames.SurveyAnswer}
                  value={$.getValueByNestedString(
                    this.state,
                    FieldNames.SurveyAnswer,
                  )}
                />
              )}
              {settings.questionnaires && (
                <InputUserInfoQuestionnaires
                  questionnaires={settings.questionnaires}
                  onChange={this.handleChange}
                  namePrefix={FieldNames.Questionnaires}
                  values={
                    $.getValueByNestedString(
                      this.state,
                      FieldNames.Questionnaires,
                    ) || {}
                  }
                />
              )}
              <BooksSubmitButton
                type="button"
                onClick={this.handleSubmit}
                value={intl.formatMessage({ id: submitLabelIntlId })}
                disabled={!submittable || !postable}
                postable={postable}
              >
                {intl.formatMessage({ id: submitLabelIntlId })}
              </BooksSubmitButton>
            </>
          )
        }
      </UserContextConsumer>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooksInputs,
}: {
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({ inputs: merchantsResourcesBooksInputs });

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    handleSubmit: (
      reservationId: number,
      userInfo: IInputs.UserInfo,
      settings: IBooks.SettingUserInfo,
    ) => inputUserInfo(reservationId, userInfo, settings)(dispatch),
    handleChange: (name: string) => clearUserInfoError(name)(dispatch),
    handleSetMe: (merchantId: string, resourceId: string) =>
      setMeToBookInfo(merchantId, resourceId)(dispatch),
    handlePostable: (postable: boolean) => setPostable(postable)(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(scrollToTop(InputUserInfo)));
